// src/pages/Orders/components/FulfillmentManager.jsx
// ENHANCED VERSION - Fully Responsive with Mobile-First Design

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  schedulePickupThunk,
  scheduleDeliveryThunk,
  reschedulePickupThunk,
  rescheduleDeliveryThunk,
  markReadyForPickupThunk,
  completePickupThunk,
  completeDeliveryThunk,
  recordDeliveryAttemptThunk,
  getFulfillmentDetailsThunk,
  clearFulfillmentDetails,
  setFulfillmentType,
} from "../../../store/thunks/orderThunks";
import Badge from "../../../components/common/Badge";
import Button from "../../../components/common/Button";
import {
  LocationMarkerIcon,
  TruckIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  RefreshIcon,
  ExclamationCircleIcon,
  UserIcon,
  DocumentTextIcon,
  PhoneIcon,
  HomeIcon,
  InformationCircleIcon,
  LightBulbIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/outline";
import { notifySuccess, notifyError } from "../../../utils/notification";

const FulfillmentManager = ({ orderId, order }) => {
  const dispatch = useDispatch();
  const { fulfillmentDetails, loadingFulfillment, fulfillmentError } =
    useSelector((state) => state.orders);

  const statusSummary = fulfillmentDetails?.statusSummary;

  const [fulfillmentType, setLocalFulfillmentType] = useState(
    order?.fulfillment?.type || "PICKUP"
  );
  const [scheduledDate, setScheduledDate] = useState("");
  const [timeSlot, setTimeSlot] = useState("MORNING");
  const [notes, setNotes] = useState("");
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [showReschedule, setShowReschedule] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    schedule: true,
    details: true,
    actions: true,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    if (order?.fulfillment?.type) {
      setLocalFulfillmentType(order.fulfillment.type);
    }
  }, [order?.fulfillment?.type]);

  const getTimeSlotObject = (slotName) => {
    const timeSlots = {
      MORNING: { start: "09:00", end: "12:00" },
      AFTERNOON: { start: "12:00", end: "17:00" },
      EVENING: { start: "17:00", end: "20:00" },
    };
    return timeSlots[slotName] || { start: "09:00", end: "12:00" };
  };

  useEffect(() => {
    if (orderId) {
      dispatch(getFulfillmentDetailsThunk(orderId));
    }
    return () => {
      dispatch(clearFulfillmentDetails());
    };
  }, [dispatch, orderId]);

  const canManageFulfillment = () => {
    const allowedStatuses = ["CONFIRMED", "PROCESSING", "READY", "DISPATCHED"];
    return allowedStatuses.includes(order?.status);
  };

  const isPickedUp = () => {
    if (fulfillmentType !== "PICKUP") return false;
    const hasActualPickupDate = !!fulfillmentDetails?.pickup?.actualPickupDate;
    const hasPickupPerson = !!fulfillmentDetails?.pickup?.pickedUpBy?.name;
    const isPickupStatus = fulfillmentDetails?.pickup?.status === "PICKED_UP";
    const isDeliveredStatus = order?.status === "DELIVERED";
    return (
      hasActualPickupDate ||
      hasPickupPerson ||
      isPickupStatus ||
      isDeliveredStatus
    );
  };

  const isDelivered = () => {
    if (fulfillmentType !== "DELIVERY") return false;
    const hasActualDeliveryDate = !!fulfillmentDetails?.delivery?.actualDate;
    const hasReceivedPerson = !!fulfillmentDetails?.delivery?.receivedBy?.name;
    const isDeliveredStatus = order?.status === "DELIVERED";
    return hasActualDeliveryDate || hasReceivedPerson || isDeliveredStatus;
  };

  const canSchedule = () => {
    if (statusSummary?.canSchedule !== undefined) {
      return statusSummary.canSchedule;
    }
    if (!canManageFulfillment()) return false;
    if (fulfillmentType === "PICKUP") {
      return !fulfillmentDetails?.pickup?.scheduledDate;
    } else {
      return !fulfillmentDetails?.delivery?.scheduledDate;
    }
  };

  const canReschedule = () => {
    if (statusSummary?.canReschedule !== undefined) {
      return statusSummary.canReschedule;
    }
    if (!canManageFulfillment()) return false;
    if (isPickedUp() || isDelivered()) return false;
    if (fulfillmentType === "PICKUP") {
      return (
        fulfillmentDetails?.pickup?.scheduledDate &&
        fulfillmentDetails?.pickup?.status !== "PICKED_UP"
      );
    } else {
      return (
        fulfillmentDetails?.delivery?.scheduledDate &&
        !fulfillmentDetails?.delivery?.actualDate
      );
    }
  };

  const isActionAvailable = (actionType) => {
    if (!canManageFulfillment()) return false;
    if (isPickedUp() || isDelivered()) return false;

    if (statusSummary) {
      switch (actionType) {
        case "MARK_READY":
          return statusSummary.canMarkReady ?? false;
        case "COMPLETE_PICKUP":
          return statusSummary.canComplete ?? false;
        case "SUCCESSFUL_DELIVERY":
        case "FAILED_DELIVERY":
        case "COMPLETE_DELIVERY":
          return statusSummary.canComplete ?? false;
        default:
          return false;
      }
    }

    switch (actionType) {
      case "MARK_READY":
        return (
          fulfillmentType === "PICKUP" &&
          fulfillmentDetails?.pickup?.status === "SCHEDULED" &&
          order?.status === "PROCESSING"
        );
      case "COMPLETE_PICKUP":
        return (
          fulfillmentType === "PICKUP" &&
          fulfillmentDetails?.pickup?.status === "READY" &&
          !isPickedUp()
        );
      case "SUCCESSFUL_DELIVERY":
      case "FAILED_DELIVERY":
      case "COMPLETE_DELIVERY":
        return (
          fulfillmentType === "DELIVERY" &&
          ["DISPATCHED", "READY"].includes(order?.status) &&
          !isDelivered()
        );
      default:
        return false;
    }
  };

  const getActionHint = (actionType) => {
    if (isPickedUp()) return "Order already picked up";
    if (isDelivered()) return "Order already delivered";

    switch (actionType) {
      case "MARK_READY":
        if (!fulfillmentDetails?.pickup?.scheduledDate)
          return "Schedule pickup first";
        if (order?.status !== "PROCESSING")
          return "Order must be in PROCESSING status";
        return "Available";
      case "COMPLETE_PICKUP":
        if (order?.status !== "READY") return "Mark order READY first";
        return "Available";
      case "COMPLETE_DELIVERY":
        if (!["DISPATCHED", "READY"].includes(order?.status))
          return "Order must be DISPATCHED";
        return "Available";
      default:
        return "";
    }
  };

  const handleSchedulePickup = async () => {
    if (!scheduledDate) {
      notifyError("Please select a date");
      return;
    }

    try {
      await dispatch(
        schedulePickupThunk({
          orderId,
          scheduledDate,
          timeSlot: getTimeSlotObject(timeSlot),
          notes,
        })
      ).unwrap();

      notifySuccess("Pickup scheduled successfully!");
      setScheduledDate("");
      setNotes("");
      dispatch(getFulfillmentDetailsThunk(orderId));
    } catch (error) {
      notifyError(error || "Failed to schedule pickup");
    }
  };

  const handleScheduleDelivery = async () => {
    if (!scheduledDate) {
      notifyError("Please select a date");
      return;
    }

    try {
      await dispatch(
        scheduleDeliveryThunk({
          orderId,
          scheduledDate,
          timeSlot: getTimeSlotObject(timeSlot),
          notes,
        })
      ).unwrap();

      notifySuccess("Delivery scheduled successfully!");
      setScheduledDate("");
      setNotes("");
      dispatch(getFulfillmentDetailsThunk(orderId));
    } catch (error) {
      notifyError(error || "Failed to schedule delivery");
    }
  };

  const handleReschedulePickup = async () => {
    if (!scheduledDate || !rescheduleReason.trim()) {
      notifyError("Please provide date and reason");
      return;
    }

    try {
      await dispatch(
        reschedulePickupThunk({
          orderId,
          newDate: scheduledDate,
          timeSlot: getTimeSlotObject(timeSlot),
          reason: rescheduleReason,
        })
      ).unwrap();

      notifySuccess("Pickup rescheduled successfully!");
      setShowReschedule(false);
      setScheduledDate("");
      setRescheduleReason("");
      dispatch(getFulfillmentDetailsThunk(orderId));
    } catch (error) {
      notifyError(error || "Failed to reschedule pickup");
    }
  };

  const handleRescheduleDelivery = async () => {
    if (!scheduledDate || !rescheduleReason.trim()) {
      notifyError("Please provide date and reason");
      return;
    }

    try {
      await dispatch(
        rescheduleDeliveryThunk({
          orderId,
          newDate: scheduledDate,
          timeSlot: getTimeSlotObject(timeSlot),
          reason: rescheduleReason,
        })
      ).unwrap();

      notifySuccess("Delivery rescheduled successfully!");
      setShowReschedule(false);
      setScheduledDate("");
      setRescheduleReason("");
      dispatch(getFulfillmentDetailsThunk(orderId));
    } catch (error) {
      notifyError(error || "Failed to reschedule delivery");
    }
  };

  const handleMarkReadyForPickup = async () => {
    try {
      await dispatch(markReadyForPickupThunk(orderId)).unwrap();
      notifySuccess("Order marked as ready for pickup!");
      dispatch(getFulfillmentDetailsThunk(orderId));
    } catch (error) {
      notifyError(error || "Failed to mark ready for pickup");
    }
  };

  const handleCompletePickup = async () => {
    setCurrentAction({
      type: "COMPLETE_PICKUP",
      title: "Complete Pickup",
      description: "Record who picked up the order",
    });
    setShowActionModal(true);
  };

  const handleRecordDeliveryAttempt = async (status) => {
    setCurrentAction({
      type: "DELIVERY_ATTEMPT",
      status,
      title:
        status === "SUCCESSFUL"
          ? "Successful Delivery"
          : "Failed Delivery Attempt",
      description: "Record the delivery attempt status",
    });
    setShowActionModal(true);
  };

  const handleCompleteDelivery = async () => {
    setCurrentAction({
      type: "COMPLETE_DELIVERY",
      title: "Complete Delivery",
      description: "Record who received the delivery",
    });
    setShowActionModal(true);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      SCHEDULED: "blue",
      RESCHEDULED: "orange",
      READY: "green",
      PICKED_UP: "green",
      DISPATCHED: "indigo",
      DELIVERED: "green",
      FAILED: "red",
    };
    return colors[status] || "gray";
  };

  const getErrorTip = (errorMessage) => {
    if (!errorMessage) return null;

    if (
      errorMessage.includes("already completed") ||
      errorMessage.includes("already picked up") ||
      errorMessage.includes("already delivered")
    ) {
      return "Cannot modify a completed order. The fulfillment is finalized.";
    }

    if (
      errorMessage.includes("must be in the future") ||
      errorMessage.includes("past")
    ) {
      return "Please select a date that's at least tomorrow or later.";
    }

    if (
      errorMessage.includes("not scheduled") ||
      errorMessage.includes("No pickup scheduled") ||
      errorMessage.includes("No delivery scheduled")
    ) {
      return "You need to schedule a pickup/delivery first before rescheduling.";
    }

    if (errorMessage.includes("must be")) {
      return "Check the order status and try again when requirements are met.";
    }

    return null;
  };

  // Loading State
  if (loadingFulfillment) {
    return (
      <div className="flex items-center justify-center min-h-[300px] sm:min-h-[400px]">
        <div className="text-center px-4">
          <div className="inline-block animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-primary border-t-transparent"></div>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600 font-medium">
            Loading fulfillment details...
          </p>
        </div>
      </div>
    );
  }

  // Not Available State
  if (!canManageFulfillment() && !isPickedUp() && !isDelivered()) {
    return (
      <div className="p-3 sm:p-4">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4 rounded-lg">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <ExclamationCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-yellow-900 text-sm sm:text-base">
                Fulfillment Not Available
              </p>
              <p className="text-yellow-800 text-xs sm:text-sm mt-1 leading-relaxed">
                Order must be in CONFIRMED, PROCESSING, READY, or DISPATCHED
                status to manage fulfillment.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-xs sm:text-sm text-yellow-700">
                  Current status:
                </span>
                <Badge variant={getStatusColor(order?.status)} size="sm">
                  {order?.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm sm:shadow-md border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-primary to-primary-dark px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white tracking-wide flex items-center gap-2">
                <TruckIcon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 flex-shrink-0" />
                <span className="truncate">Fulfillment Manager</span>
              </h2>
              <p className="text-white/80 mt-0.5 sm:mt-1 text-xs sm:text-sm truncate">
                Order #{orderId}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => dispatch(getFulfillmentDetailsThunk(orderId))}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 p-2 sm:p-2.5"
            >
              <RefreshIcon className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {fulfillmentError && (
          <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 bg-red-50 border-b border-red-200">
            <div className="flex items-start gap-2.5 sm:gap-3">
              <ExclamationCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-red-900 text-xs sm:text-sm">
                  {fulfillmentError.includes("Cannot") ||
                  fulfillmentError.includes("already")
                    ? "Action Not Allowed"
                    : "Error"}
                </p>
                <p className="text-red-700 text-xs sm:text-sm mt-0.5 break-words">
                  {fulfillmentError}
                </p>

                {getErrorTip(fulfillmentError) && (
                  <div className="mt-2 sm:mt-3 flex items-start gap-2 bg-red-100 border border-red-200 rounded-lg p-2 sm:p-3">
                    <LightBulbIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800 text-[10px] sm:text-xs font-medium">
                      💡 Tip: {getErrorTip(fulfillmentError)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Completion Alerts */}
      {isPickedUp() && (
        <CompletionAlert
          type="pickup"
          details={fulfillmentDetails?.pickup}
          formatDateTime={formatDateTime}
        />
      )}

      {isDelivered() && (
        <CompletionAlert
          type="delivery"
          details={fulfillmentDetails?.delivery}
          formatDateTime={formatDateTime}
        />
      )}

      {/* Fulfillment Type Selector */}
      {!isPickedUp() && !isDelivered() && (
        <CollapsibleSection
          title="Fulfillment Method"
          icon={DocumentTextIcon}
          expanded={expandedSections.schedule}
          onToggle={() => toggleSection("schedule")}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <FulfillmentTypeCard
              type="PICKUP"
              selected={fulfillmentType === "PICKUP"}
              onSelect={(type) => {
                setLocalFulfillmentType(type);
                dispatch(setFulfillmentType(type));
              }}
              icon={LocationMarkerIcon}
              title="Store Pickup"
              description="Customer collects order from store"
            />
            <FulfillmentTypeCard
              type="DELIVERY"
              selected={fulfillmentType === "DELIVERY"}
              onSelect={(type) => {
                setLocalFulfillmentType(type);
                dispatch(setFulfillmentType(type));
              }}
              icon={TruckIcon}
              title="Home Delivery"
              description="Deliver order to customer's address"
            />
          </div>
        </CollapsibleSection>
      )}

      {/* Delivery Address */}
      {fulfillmentType === "DELIVERY" &&
        !isDelivered() &&
        order?.fulfillment?.delivery?.address && (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm sm:shadow-md border border-gray-200 overflow-hidden">
            <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-gray-100">
              <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
                <HomeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                Delivery Address
              </h3>
            </div>
            <div className="p-3 sm:p-4 md:p-6">
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-blue-100">
                <p className="font-semibold text-gray-900 text-sm sm:text-base">
                  {order.fulfillment.delivery.address.fullName}
                </p>
                <p className="text-xs sm:text-sm text-gray-700 mt-1.5 sm:mt-2">
                  {order.fulfillment.delivery.address.street}
                  {order.fulfillment.delivery.address.apartment && (
                    <>, {order.fulfillment.delivery.address.apartment}</>
                  )}
                </p>
                <p className="text-xs sm:text-sm text-gray-700">
                  {order.fulfillment.delivery.address.city},{" "}
                  {order.fulfillment.delivery.address.state} -{" "}
                  {order.fulfillment.delivery.address.postalCode}
                </p>
                {order.fulfillment.delivery.address.phone && (
                  <p className="text-xs sm:text-sm text-gray-700 mt-2 flex items-center gap-1.5">
                    <PhoneIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {order.fulfillment.delivery.address.phone}
                  </p>
                )}
                {order.fulfillment.delivery.address.landmark && (
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-1.5 sm:mt-2 bg-white/50 rounded px-2 py-1">
                    📍 Landmark: {order.fulfillment.delivery.address.landmark}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Schedule Section */}
      {!showReschedule && !isPickedUp() && !isDelivered() && canSchedule() && (
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm sm:shadow-md border border-gray-200 overflow-hidden">
          <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-b border-gray-100">
            <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              {fulfillmentType === "PICKUP"
                ? "Schedule Pickup"
                : "Schedule Delivery"}
            </h3>
          </div>

          <div className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
            {/* Date & Time Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Date <span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors outline-none text-gray-900"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Time Slot <span className="text-red-600">*</span>
                </label>
                <select
                  value={timeSlot}
                  onChange={(e) => setTimeSlot(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors outline-none text-gray-900 bg-white"
                >
                  <option value="MORNING">🌅 Morning (9AM - 12PM)</option>
                  <option value="AFTERNOON">☀️ Afternoon (12PM - 5PM)</option>
                  <option value="EVENING">🌆 Evening (5PM - 8PM)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                Special Instructions
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special instructions or notes..."
                rows={2}
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors outline-none text-gray-900 resize-none"
              />
            </div>

            <Button
              onClick={
                fulfillmentType === "PICKUP"
                  ? handleSchedulePickup
                  : handleScheduleDelivery
              }
              disabled={!scheduledDate}
              variant="primary"
              className="w-full py-2.5 sm:py-3 text-sm sm:text-base"
            >
              <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              {fulfillmentType === "PICKUP"
                ? "Schedule Pickup"
                : "Schedule Delivery"}
            </Button>
          </div>
        </div>
      )}

      {/* Reschedule Section */}
      {showReschedule && canReschedule() && !isPickedUp() && !isDelivered() && (
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm sm:shadow-md border-2 border-orange-300 overflow-hidden">
          <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 bg-orange-50 border-b border-orange-200">
            <h3 className="text-sm sm:text-base md:text-lg font-bold text-orange-900 flex items-center gap-2">
              <RefreshIcon className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
              {fulfillmentType === "PICKUP"
                ? "Reschedule Pickup"
                : "Reschedule Delivery"}
            </h3>
          </div>

          <div className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  New Date <span className="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-orange-300 rounded-lg sm:rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors outline-none text-gray-900"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  New Time Slot <span className="text-red-600">*</span>
                </label>
                <select
                  value={timeSlot}
                  onChange={(e) => setTimeSlot(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-orange-300 rounded-lg sm:rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors outline-none text-gray-900 bg-white"
                >
                  <option value="MORNING">🌅 Morning (9AM - 12PM)</option>
                  <option value="AFTERNOON">☀️ Afternoon (12PM - 5PM)</option>
                  <option value="EVENING">🌆 Evening (5PM - 8PM)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                Reason for Rescheduling <span className="text-red-600">*</span>
              </label>
              <textarea
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                placeholder="Please provide a reason for rescheduling..."
                rows={2}
                required
                className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-orange-300 rounded-lg sm:rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors outline-none text-gray-900 resize-none"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                onClick={
                  fulfillmentType === "PICKUP"
                    ? handleReschedulePickup
                    : handleRescheduleDelivery
                }
                disabled={!scheduledDate || !rescheduleReason}
                variant="primary"
                className="flex-1 bg-orange-600 hover:bg-orange-700 py-2.5 sm:py-3 text-sm sm:text-base order-1 sm:order-1"
              >
                <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Confirm Reschedule
              </Button>
              <Button
                onClick={() => {
                  setShowReschedule(false);
                  setScheduledDate("");
                  setRescheduleReason("");
                }}
                variant="outline"
                className="flex-1 py-2.5 sm:py-3 text-sm sm:text-base order-2 sm:order-2"
              >
                <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Current Fulfillment Details */}
      {fulfillmentDetails && (
        <CollapsibleSection
          title="Current Fulfillment Status"
          icon={DocumentTextIcon}
          expanded={expandedSections.details}
          onToggle={() => toggleSection("details")}
          headerBg="bg-gradient-to-r from-blue-50 to-indigo-50"
        >
          {fulfillmentType === "PICKUP" && fulfillmentDetails.pickup && (
            <FulfillmentDetailsCard
              type="pickup"
              details={fulfillmentDetails.pickup}
              isCompleted={isPickedUp()}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
              getStatusColor={getStatusColor}
            />
          )}

          {fulfillmentType === "DELIVERY" && fulfillmentDetails.delivery && (
            <FulfillmentDetailsCard
              type="delivery"
              details={fulfillmentDetails.delivery}
              isCompleted={isDelivered()}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
              getStatusColor={getStatusColor}
            />
          )}
        </CollapsibleSection>
      )}

      {/* Change History Section */}
      {fulfillmentDetails?.changeHistory &&
        fulfillmentDetails.changeHistory.length > 0 &&
        !isPickedUp() &&
        !isDelivered() && (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm sm:shadow-md border border-gray-200 overflow-hidden">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full px-3 sm:px-4 md:px-6 py-3 sm:py-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-200 flex items-center justify-between"
            >
              <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
                <ClockIcon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                Schedule History
                <Badge variant="gray" size="sm">
                  {fulfillmentDetails.changeHistory.length}
                </Badge>
              </h3>
              {showHistory ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-500" />
              )}
            </button>

            {showHistory && (
              <div className="p-3 sm:p-4 md:p-6">
                <div className="space-y-2 sm:space-y-3">
                  {fulfillmentDetails.changeHistory.map((change, index) => (
                    <HistoryItem
                      key={index}
                      change={change}
                      formatDate={formatDate}
                      formatDateTime={formatDateTime}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      {/* Quick Actions */}
      {!isPickedUp() && !isDelivered() && (
        <CollapsibleSection
          title="Quick Actions"
          icon={CheckCircleIcon}
          expanded={expandedSections.actions}
          onToggle={() => toggleSection("actions")}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
            {fulfillmentType === "PICKUP" && (
              <>
                <ActionButton
                  onClick={handleMarkReadyForPickup}
                  available={isActionAvailable("MARK_READY")}
                  icon={<CheckCircleIcon className="w-4 h-4" />}
                  label="Ready for Pickup"
                  hint={getActionHint("MARK_READY")}
                  variant="outline"
                  className="border-green-500 text-green-700 hover:bg-green-50"
                />

                <ActionButton
                  onClick={handleCompletePickup}
                  available={isActionAvailable("COMPLETE_PICKUP")}
                  icon={<LocationMarkerIcon className="w-4 h-4" />}
                  label="Complete Pickup"
                  hint={getActionHint("COMPLETE_PICKUP")}
                  variant="primary"
                  className="bg-green-600 hover:bg-green-700"
                />
              </>
            )}

            {fulfillmentType === "DELIVERY" && (
              <>
                <ActionButton
                  onClick={() => handleRecordDeliveryAttempt("SUCCESSFUL")}
                  available={isActionAvailable("SUCCESSFUL_DELIVERY")}
                  icon={<CheckCircleIcon className="w-4 h-4" />}
                  label="Successful Delivery"
                  hint={getActionHint("COMPLETE_DELIVERY")}
                  variant="primary"
                  className="bg-green-600 hover:bg-green-700"
                />

                <ActionButton
                  onClick={() => handleRecordDeliveryAttempt("FAILED")}
                  available={isActionAvailable("FAILED_DELIVERY")}
                  icon={<XCircleIcon className="w-4 h-4" />}
                  label="Failed Attempt"
                  hint={getActionHint("COMPLETE_DELIVERY")}
                  variant="outline"
                  className="border-red-500 text-red-700 hover:bg-red-50"
                />

                <ActionButton
                  onClick={handleCompleteDelivery}
                  available={isActionAvailable("COMPLETE_DELIVERY")}
                  icon={<TruckIcon className="w-4 h-4" />}
                  label="Complete Delivery"
                  hint={getActionHint("COMPLETE_DELIVERY")}
                  variant="primary"
                />
              </>
            )}

            {canReschedule() && (
              <Button
                onClick={() => setShowReschedule(!showReschedule)}
                variant="outline"
                className="border-orange-500 text-orange-700 hover:bg-orange-50 py-2.5 sm:py-3 text-xs sm:text-sm"
              >
                <RefreshIcon className="w-4 h-4 mr-2" />
                {showReschedule ? "Cancel" : "Reschedule"}
              </Button>
            )}
          </div>

          {/* No Actions Message */}
          {!isActionAvailable("MARK_READY") &&
            !isActionAvailable("COMPLETE_PICKUP") &&
            !isActionAvailable("SUCCESSFUL_DELIVERY") &&
            !isActionAvailable("FAILED_DELIVERY") &&
            !isActionAvailable("COMPLETE_DELIVERY") &&
            !canReschedule() && (
              <div className="text-center py-6 sm:py-8 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200 mt-3 sm:mt-4">
                <InformationCircleIcon className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-xs sm:text-sm text-gray-600 font-medium">
                  No actions available for current status
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
                  Status:{" "}
                  <Badge variant="gray" size="sm">
                    {order?.status}
                  </Badge>
                </p>
              </div>
            )}
        </CollapsibleSection>
      )}

      {/* Completion Card */}
      {(isPickedUp() || isDelivered()) && (
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm sm:shadow-md border-2 border-green-200 overflow-hidden">
          <div className="p-6 sm:p-8 md:p-10 text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 mx-auto mb-3 sm:mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircleIcon className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-green-500" />
            </div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-green-900 mb-1 sm:mb-2">
              {isPickedUp() ? "Order Picked Up!" : "Order Delivered!"}
            </h3>
            <p className="text-xs sm:text-sm md:text-base text-gray-600">
              This order has been successfully{" "}
              {isPickedUp() ? "picked up" : "delivered"}.
            </p>
            {isPickedUp() && fulfillmentDetails?.pickup?.actualPickupDate && (
              <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 mt-2">
                Completed on{" "}
                {formatDateTime(fulfillmentDetails.pickup.actualPickupDate)}
              </p>
            )}
            {isDelivered() && fulfillmentDetails?.delivery?.actualDate && (
              <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 mt-2">
                Completed on{" "}
                {formatDateTime(fulfillmentDetails.delivery.actualDate)}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && currentAction && (
        <ActionModal
          action={currentAction}
          orderId={orderId}
          onClose={() => {
            setShowActionModal(false);
            setCurrentAction(null);
          }}
          dispatch={dispatch}
          onSuccess={() => {
            dispatch(getFulfillmentDetailsThunk(orderId));
          }}
        />
      )}
    </div>
  );
};

// ==========================================
// SUB-COMPONENTS
// ==========================================

// Collapsible Section Component
const CollapsibleSection = ({
  title,
  icon: Icon,
  expanded,
  onToggle,
  children,
  headerBg = "bg-white",
}) => (
  <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm sm:shadow-md border border-gray-200 overflow-hidden">
    <button
      onClick={onToggle}
      className={`w-full px-3 sm:px-4 md:px-6 py-3 sm:py-4 ${headerBg} border-b border-gray-100 flex items-center justify-between`}
    >
      <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />}
        {title}
      </h3>
      {expanded ? (
        <ChevronUpIcon className="w-5 h-5 text-gray-500" />
      ) : (
        <ChevronDownIcon className="w-5 h-5 text-gray-500" />
      )}
    </button>
    {expanded && <div className="p-3 sm:p-4 md:p-6">{children}</div>}
  </div>
);

// Fulfillment Type Card
const FulfillmentTypeCard = ({
  type,
  selected,
  onSelect,
  icon: Icon,
  title,
  description,
}) => (
  <label
    className={`relative flex items-start p-3 sm:p-4 cursor-pointer rounded-lg sm:rounded-xl border-2 transition-all duration-200 ${
      selected
        ? "border-primary bg-primary/5 shadow-md"
        : "border-gray-200 bg-white hover:border-primary/50 hover:bg-gray-50"
    }`}
  >
    <input
      type="radio"
      value={type}
      checked={selected}
      onChange={(e) => onSelect(e.target.value)}
      className="w-4 h-4 sm:w-5 sm:h-5 text-primary focus:ring-primary focus:ring-2 mt-0.5"
    />
    <div className="ml-2.5 sm:ml-3 flex-1 min-w-0">
      <div className="flex items-center gap-1.5 sm:gap-2">
        <Icon
          className={`w-4 h-4 sm:w-5 sm:h-5 ${
            selected ? "text-primary" : "text-gray-400"
          }`}
        />
        <span className="text-sm sm:text-base font-semibold text-gray-900">
          {title}
        </span>
      </div>
      <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 mt-0.5 sm:mt-1">
        {description}
      </p>
    </div>
    {selected && (
      <Badge
        variant="green"
        size="sm"
        className="absolute top-2 right-2 sm:top-3 sm:right-3 text-[9px] sm:text-[10px]"
      >
        Selected
      </Badge>
    )}
  </label>
);

// Completion Alert Component
const CompletionAlert = ({ type, details, formatDateTime }) => {
  const isPickup = type === "pickup";
  const person = isPickup ? details?.pickedUpBy : details?.receivedBy;
  const date = isPickup ? details?.actualPickupDate : details?.actualDate;

  return (
    <div className="bg-green-50 border-l-4 border-green-500 p-3 sm:p-4 rounded-lg sm:rounded-xl">
      <div className="flex items-start gap-2.5 sm:gap-3">
        <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-green-900 text-sm sm:text-base">
            {isPickup ? "Order Picked Up" : "Order Delivered"}
          </p>
          <p className="text-green-800 text-xs sm:text-sm mt-1">
            {isPickup ? "Picked up" : "Received"} by{" "}
            <strong>{person?.name}</strong> on{" "}
            <strong>{formatDateTime(date)}</strong>
            {person?.relation && <> (Relation: {person.relation})</>}
          </p>
          {person?.phone && person.phone !== "N/A" && (
            <p className="text-green-700 text-[10px] sm:text-xs mt-1 flex items-center gap-1">
              <PhoneIcon className="w-3 h-3" />
              {person.phone}
            </p>
          )}
          {details?.trackingNumber && (
            <p className="text-green-700 text-[10px] sm:text-xs mt-2">
              Tracking: {details.trackingNumber}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Fulfillment Details Card
const FulfillmentDetailsCard = ({
  type,
  details,
  isCompleted,
  formatDate,
  formatDateTime,
  getStatusColor,
}) => {
  const isPickup = type === "pickup";
  const Icon = isPickup ? LocationMarkerIcon : TruckIcon;
  const bgColor = isPickup
    ? "from-gray-50 to-blue-50"
    : "from-gray-50 to-green-50";
  const borderColor = isPickup ? "border-blue-100" : "border-green-100";
  const iconColor = isPickup ? "text-blue-600" : "text-green-600";

  return (
    <div
      className={`bg-gradient-to-br ${bgColor} rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-5 border ${borderColor}`}
    >
      <h4 className="text-sm sm:text-base font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
        <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${iconColor}`} />
        {isPickup ? "Pickup Details" : "Delivery Details"}
        {isCompleted && (
          <Badge
            variant="green"
            size="sm"
            className="ml-auto text-[9px] sm:text-[10px]"
          >
            Completed
          </Badge>
        )}
      </h4>

      <div className="space-y-2 sm:space-y-3">
        <DetailRow
          label="Status"
          value={
            <Badge
              variant={isCompleted ? "green" : getStatusColor(details.status)}
              size="sm"
            >
              {isCompleted
                ? isPickup
                  ? "PICKED_UP"
                  : "DELIVERED"
                : details.status}
            </Badge>
          }
        />

        {details.scheduledDate && (
          <DetailRow
            label="Scheduled Date"
            value={formatDate(details.scheduledDate)}
            icon={CalendarIcon}
          />
        )}

        {details.scheduledTimeSlot?.start && (
          <DetailRow
            label="Time Slot"
            value={`${details.scheduledTimeSlot.start} - ${details.scheduledTimeSlot.end}`}
            icon={ClockIcon}
          />
        )}

        {isPickup && details.readyForPickupAt && (
          <DetailRow
            label="Ready Since"
            value={formatDateTime(details.readyForPickupAt)}
            valueClass="text-green-700 font-semibold"
          />
        )}

        {isPickup && details.actualPickupDate && (
          <DetailRow
            label="✅ Picked Up On"
            value={formatDateTime(details.actualPickupDate)}
            valueClass="text-green-700 font-bold"
          />
        )}

        {!isPickup && details.actualDate && (
          <DetailRow
            label="✅ Delivered On"
            value={formatDateTime(details.actualDate)}
            valueClass="text-green-700 font-bold"
          />
        )}

        {!isPickup && details.attemptCount > 0 && (
          <DetailRow
            label="Delivery Attempts"
            value={
              <Badge variant="orange" size="sm">
                {details.attemptCount} attempts
              </Badge>
            }
          />
        )}

        {/* Person Info */}
        {(isPickup ? details.pickedUpBy?.name : details.receivedBy?.name) && (
          <div className="pt-2 sm:pt-3 border-t border-gray-200">
            <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2 flex items-center gap-1">
              <UserIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              {isPickup ? "Picked Up By:" : "Received By:"}
            </p>
            <div className="bg-white rounded-lg p-2.5 sm:p-3 border-2 border-green-200 shadow-sm">
              <p className="font-semibold text-green-900 text-sm sm:text-base">
                {isPickup ? details.pickedUpBy.name : details.receivedBy.name}
              </p>
              {isPickup &&
                details.pickedUpBy.phone &&
                details.pickedUpBy.phone !== "N/A" && (
                  <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1 mt-1">
                    <PhoneIcon className="w-3 h-3" />
                    {details.pickedUpBy.phone}
                  </p>
                )}
              {(isPickup
                ? details.pickedUpBy.relation
                : details.receivedBy.relation) && (
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Relation:{" "}
                  {isPickup
                    ? details.pickedUpBy.relation
                    : details.receivedBy.relation}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Notes / Tracking */}
        {isPickup && details.pickupNotes && (
          <div className="pt-2 sm:pt-3 border-t border-gray-200">
            <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">
              Notes:
            </p>
            <p className="text-xs sm:text-sm text-gray-600 bg-white rounded p-2 border border-blue-100">
              {details.pickupNotes}
            </p>
          </div>
        )}

        {!isPickup && details.trackingNumber && (
          <div className="pt-2 sm:pt-3 border-t border-gray-200">
            <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">
              Tracking Number:
            </p>
            <p className="text-xs sm:text-sm font-mono text-blue-600 bg-white rounded p-2 border border-green-100">
              {details.trackingNumber}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Detail Row Component
const DetailRow = ({
  label,
  value,
  icon: Icon,
  valueClass = "text-gray-900 font-semibold",
}) => (
  <div className="flex items-center justify-between py-1.5 sm:py-2 border-b border-gray-200/50 last:border-0">
    <span className="text-[10px] sm:text-xs md:text-sm text-gray-600 font-medium flex items-center gap-1">
      {Icon && <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
      {label}:
    </span>
    <span className={`text-[10px] sm:text-xs md:text-sm ${valueClass}`}>
      {value}
    </span>
  </div>
);

// History Item Component
const HistoryItem = ({ change, formatDate, formatDateTime }) => (
  <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-purple-50 rounded-lg sm:rounded-xl border border-purple-100">
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
        <Badge
          variant={change.changeType === "INITIAL" ? "green" : "orange"}
          size="sm"
          className="text-[9px] sm:text-[10px]"
        >
          {change.changeType === "INITIAL" ? "📅 Initial" : "✏️ Rescheduled"}
        </Badge>
        <span className="text-[9px] sm:text-[10px] text-gray-500">
          {formatDateTime(change.changedAt)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
        <UserIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
        <p className="text-[10px] sm:text-xs md:text-sm text-gray-700">
          {change.changeType === "INITIAL" ? "Scheduled" : "Rescheduled"} by{" "}
          <strong className="text-gray-900">
            {change.changedBy?.name || "Unknown"}
          </strong>
        </p>
        {change.changedBy?.type && (
          <Badge size="sm" variant="gray" className="text-[8px] sm:text-[9px]">
            {change.changedBy.type}
          </Badge>
        )}
      </div>

      {change.oldDate && change.newDate && (
        <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-600 mt-1.5 sm:mt-2 flex items-center gap-1 flex-wrap">
          <CalendarIcon className="w-3 h-3 flex-shrink-0" />
          <span className="line-through">{formatDate(change.oldDate)}</span>
          <span>→</span>
          <span className="font-semibold">{formatDate(change.newDate)}</span>
        </p>
      )}

      {!change.oldDate && change.newDate && (
        <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-600 mt-1.5 sm:mt-2 flex items-center gap-1">
          <CalendarIcon className="w-3 h-3" />
          Date: <strong>{formatDate(change.newDate)}</strong>
        </p>
      )}

      {change.reason && (
        <p className="text-[9px] sm:text-[10px] md:text-xs text-gray-600 mt-1.5 sm:mt-2 italic bg-white rounded px-2 py-1 border border-purple-100">
          Reason: {change.reason}
        </p>
      )}
    </div>
  </div>
);

// Action Button Component with Tooltip
const ActionButton = ({
  onClick,
  available,
  icon,
  label,
  hint,
  variant,
  className,
}) => {
  return (
    <div className="relative group">
      <Button
        onClick={onClick}
        disabled={!available}
        variant={variant}
        className={`w-full py-2.5 sm:py-3 text-xs sm:text-sm ${className} ${
          !available ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {icon}
        <span className="ml-1.5 sm:ml-2">{label}</span>
      </Button>

      {!available && hint && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-20">
          <div className="bg-gray-900 text-white text-[10px] sm:text-xs rounded-lg py-1.5 sm:py-2 px-2 sm:px-3 whitespace-nowrap shadow-lg max-w-[200px] text-center">
            ⏸️ {hint}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Action Modal Component
const ActionModal = ({ action, orderId, onClose, dispatch, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    relation: "",
    reason: "",
    nextAttemptDate: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (
      (action.type === "COMPLETE_PICKUP" ||
        action.type === "COMPLETE_DELIVERY") &&
      !formData.name.trim()
    ) {
      notifyError("Please provide a name");
      return;
    }

    if (action.type === "DELIVERY_ATTEMPT" && !formData.reason.trim()) {
      notifyError("Please provide a reason");
      return;
    }

    setLoading(true);
    try {
      if (action.type === "COMPLETE_PICKUP") {
        await dispatch(
          completePickupThunk({
            orderId,
            pickedUpBy: {
              name: formData.name,
              phone: formData.phone,
              relation: formData.relation,
            },
          })
        ).unwrap();
        notifySuccess("Pickup completed successfully!");
      } else if (action.type === "COMPLETE_DELIVERY") {
        await dispatch(
          completeDeliveryThunk({
            orderId,
            receivedBy: {
              name: formData.name,
              relation: formData.relation,
            },
            signature: null,
            proofOfDelivery: null,
          })
        ).unwrap();
        notifySuccess("Delivery completed successfully!");
      } else if (action.type === "DELIVERY_ATTEMPT") {
        await dispatch(
          recordDeliveryAttemptThunk({
            orderId,
            status: action.status,
            reason: formData.reason,
            nextAttemptDate:
              action.status === "FAILED" ? formData.nextAttemptDate : null,
          })
        ).unwrap();
        notifySuccess("Delivery attempt recorded!");
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      notifyError(error || "Failed to complete action");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primary to-primary-dark px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg font-semibold text-white truncate">
              {action.title}
            </h3>
            <p className="text-white/80 text-xs sm:text-sm mt-0.5 truncate">
              {action.description}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 sm:p-2 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0 ml-2"
          >
            <XCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 sm:space-y-4">
          {(action.type === "COMPLETE_PICKUP" ||
            action.type === "COMPLETE_DELIVERY") && (
            <>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Enter name"
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  required
                />
              </div>

              {action.type === "COMPLETE_PICKUP" && (
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    placeholder="Enter phone number (optional)"
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Relation to Customer
                </label>
                <input
                  type="text"
                  value={formData.relation}
                  onChange={(e) =>
                    setFormData({ ...formData, relation: e.target.value })
                  }
                  placeholder="e.g., Self, Spouse, Family Member"
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </div>
            </>
          )}

          {action.type === "DELIVERY_ATTEMPT" && (
            <>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                  Reason <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) =>
                    setFormData({ ...formData, reason: e.target.value })
                  }
                  placeholder="Enter reason for delivery attempt status"
                  rows={3}
                  className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none"
                  required
                />
              </div>

              {action.status === "FAILED" && (
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    Next Attempt Date
                  </label>
                  <input
                    type="date"
                    value={formData.nextAttemptDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        nextAttemptDate: e.target.value,
                      })
                    }
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg sm:rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-2.5 sm:py-3 text-sm sm:text-base"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={
              loading ||
              (action.type !== "DELIVERY_ATTEMPT" && !formData.name) ||
              (action.type === "DELIVERY_ATTEMPT" && !formData.reason)
            }
            className="flex-1 py-2.5 sm:py-3 text-sm sm:text-base"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <ClockIcon className="w-4 h-4 animate-spin" />
                Processing...
              </span>
            ) : (
              <>
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                Confirm
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FulfillmentManager;
